import { Component, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { WaitingListRatingUpsertDialogStore } from './waiting-list-rating-upsert-dialog.store';
import { BaseDialogComponent } from '../../../kit/base-dialog/base-dialog.component';
import { AppFormGroup } from '../../../../utils/form.utils';
import { fb } from '../../../../modules/shared/shared.global-service';
import { WaitingListRatingObject } from '@libs/types/src/services/object-type/waiting-list-rating.object';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { WaitingListObject } from '@libs/types/src/services/object-type/waiting-list.object';
import {
    ServiceWaitingListObject,
} from '@libs/types/src/services/object-type/service/settings/service-waiting-list.object';
import {
    ServiceWaitingListUnitEnum,
    serviceWaitingListUnitEnumValues,
} from '@libs/types/src/services/enum/service-waiting-list-unit.enum';
import { SelectInputComponent } from '../../../kit/input/select-input/select-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { DateInputComponent } from '../../../kit/input/date-input/date-input.component';
import { Validators } from '@angular/forms';
import { isInvalid } from '../../../../modules/shared/helpers/validate';
import { contentGlobalSuccess } from '../../../../modules/content/content.error';
import { WaitingListRatingUpdateInput } from '@libs/types/src/services/input-type/waiting-list-rating-update.input';
import { WaitingListRatingCreateInput } from '@libs/types/src/services/input-type/waiting-list-rating-create.input';
import { createRatingsFrag, updateRatingsFrag } from './waiting-list-rating-upsert-dialog.query';

@Component({
    selector: 'app-waiting-list-rating-upsert-dialog',
    templateUrl: './waiting-list-rating-upsert-dialog.component.html',
    styleUrls: ['./waiting-list-rating-upsert-dialog.component.scss'],
    providers: [WaitingListRatingUpsertDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectInputComponent,
        TranslatePipe,
        DateInputComponent,
    ],
})
export class WaitingListRatingUpsertDialogComponent extends BaseDialogComponent {

    public readonly store = inject(WaitingListRatingUpsertDialogStore);

    protected form = new AppFormGroup({
        date: fb.control<Date>(new Date(), Validators.required),
        ratings: fb.array([]),
    });
    protected formRatingsChanged = this.form.signalCodeChange('ratings');

    protected prefix = 'waitingList.attendant.ratings.detail.';

    protected totalPoints = computed(() => {
        return (this.formRatingsChanged().value as any[]).filter((it: any) => !!it.rating).map((it: any) => it.rating).reduce((acc, n) => acc + n, 0);
    });

    protected waitingList!: WaitingListObject;
    protected item?: WaitingListRatingObject | null;

    constructor() {
        super();

        this.waitingList = this.dialogConfig.data.waitingList;
        this.item = this.dialogConfig.data.item;

        if (this.item) {
            this.form.patchValue({ date: new Date(this.item.date) });
        }

        effect(() => {
            this.fetchServiceRatings();
        });

        effect(() => {
            const ratings = this.store.data.getServiceWaitingListItems()?.['items'] || [];

            this.form.controls.ratings.clear();

            if (ratings) {
                const itemRatings = JSON.parse(this.item?.rating || '{}');

                ratings.forEach((r: ServiceWaitingListObject) => {
                    const ratingEntry = ('values' in itemRatings) && itemRatings.values.find((x: any) => x.serviceSettingsWaitingListId === r.id);

                    const fg = fb.group({
                        id: fb.control<number>(r.id),
                        rating: fb.control<number | null>(ratingEntry?.points || null, r.required ? Validators.required : []),
                        type: fb.control<ServiceWaitingListUnitEnum>(r.type as ServiceWaitingListUnitEnum),
                        name: fb.control<string | null>(r.name),
                        description: fb.control<string | null>(r.description),
                        isRequired: fb.control<boolean>(r.required),
                    });
                    this.form.controls.ratings.push(fg as any);
                });
            }
        });
    }

    public override async save(_event: Event) {
        if (isInvalid(this.form)) return;

        const ratings = {
            values: this.form.value.ratings?.map((it: any) => {
                return { serviceSettingsWaitingListId: it.id, points: it.rating };
            }) || [],
        };

        const _data = {
            date: this.form.value.date || '',
            rating: JSON.stringify(ratings),
        };

        if (this.item) {
            const data = _data as unknown as WaitingListRatingUpdateInput;
            data.id = this.item.id;
            await this.store.api.updateWaitingListRating(data, updateRatingsFrag);
            contentGlobalSuccess.next('common.updated');
        } else {
            const data = _data as unknown as WaitingListRatingCreateInput;
            data.waitingListId = this.waitingList.id;
            await this.store.api.createWaitingListRating(data, createRatingsFrag);
            contentGlobalSuccess.next('common.created');
        }

        this.dialogRef.close();
    }

    private async fetchServiceRatings() {
        const filter = new FilterBuilder({
            serviceId: this.waitingList.folder.serviceId,
        });
        filter.validByNow('dateFrom', 'dateTo');

        await this.store.api.getServiceWaitingListItems(filter.filter());
    }

    protected readonly serviceWaitingListUnitEnumValues = serviceWaitingListUnitEnumValues;
}
